/**
 * Record encryption for data at rest on the device (ADR-0021).
 *
 * AES-GCM-256 with a fresh 96-bit IV per write. Each envelope is bound to the
 * record it belongs to through AES-GCM additional authenticated data (AAD =
 * "<table>:<primary key>"), so ciphertext cannot be swapped between records
 * and any tampering fails authentication instead of decrypting to garbage.
 */

/** What is stored in place of a sensitive value. */
export interface Envelope {
  v: 1;
  iv: Uint8Array;
  ciphertext: ArrayBuffer;
}

/** Stored data failed authentication: tampered, swapped, or encrypted under a key this device no longer has. */
export class DataIntegrityError extends Error {
  readonly code = 'DATA_INTEGRITY';
  constructor(readonly aad: string) {
    super(`Encrypted data for ${aad} could not be decrypted (tampered, or the device key changed)`);
    this.name = 'DataIntegrityError';
  }
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

// Realm-agnostic checks: values coming back from IndexedDB's structured clone
// may be typed arrays from another realm, where `instanceof` fails.
const tag = (x: unknown) => Object.prototype.toString.call(x);

export function isEnvelope(x: unknown): x is Envelope {
  if (!x || typeof x !== 'object') return false;
  const e = x as Partial<Envelope>;
  return e.v === 1 && tag(e.iv) === '[object Uint8Array]' && e.iv!.byteLength === 12 && tag(e.ciphertext) === '[object ArrayBuffer]';
}

export class RecordCipher {
  constructor(private readonly key: CryptoKey) {}

  async seal(plain: ArrayBuffer | Uint8Array, aad: string): Promise<Envelope> {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, additionalData: encoder.encode(aad) },
      this.key,
      plain as BufferSource,
    );
    return { v: 1, iv, ciphertext };
  }

  async open(env: Envelope, aad: string): Promise<ArrayBuffer> {
    if (!isEnvelope(env)) throw new DataIntegrityError(aad);
    try {
      return await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: env.iv as BufferSource, additionalData: encoder.encode(aad) },
        this.key,
        env.ciphertext,
      );
    } catch {
      throw new DataIntegrityError(aad);
    }
  }

  /** JSON values; `undefined` is stored as `null`. */
  sealJson(value: unknown, aad: string): Promise<Envelope> {
    return this.seal(encoder.encode(JSON.stringify(value === undefined ? null : value)), aad);
  }

  async openJson<T>(env: Envelope, aad: string): Promise<T> {
    return JSON.parse(decoder.decode(await this.open(env, aad))) as T;
  }
}
